import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie,
  faMoneyBillWave,
  faChartLine,
  faUserCheck,
  faCity,
  faLaptop,
  faVenusMars,
  faBirthdayCake,
  faArrowRight,
  faChartBar,
} from '@fortawesome/free-solid-svg-icons';

import clientAxios from '../../clients/clientAxios';
import LoadingScreen from '../../Layout/LoadingScreen';
import Incomes from './Incomes';
import StudentsCounter from './StudentsCounter';
import Cities from './Cities';
import Gender from './Gender';
import AverageAges from './AverageAges';
import ClassroomStatistics from './ClassroomStatistics';
import FirstInstallmentMetrics from './FirstInstallmentMetrics';
import {
  USER_LEVEL_START,
  USER_LEVEL_STATISTICS,
  USER_LEVEL_MENTOR_STATISTICS,
  TAB_SALES_METRICS,
} from '../../constants';

const TAB_NONE = '';
const TAB_INCOMES = 'incomes';
const TAB_STUDENTS_COUNTER = 'studentsCounter';
const TAB_CITIES = 'cities';
const TAB_CLASSROOMS = 'classrooms';
const TAB_GENDER = 'gender';
const TAB_AGES = 'ages';

interface StatisticTabItem {
  key: string;
  title: string;
  icon: any;
  accentColor: string;
  accentBg: string;
  description: string;
}

const STATISTIC_TABS_CONFIG: StatisticTabItem[] = [
  {
    key: TAB_INCOMES,
    title: 'Ingresos',
    icon: faMoneyBillWave,
    accentColor: '#059669',
    accentBg: '#ECFDF5',
    description: 'Análisis de facturación, cobros e ingresos totales',
  },
  {
    key: TAB_SALES_METRICS,
    title: 'Métricas de ventas',
    icon: faChartLine,
    accentColor: '#2563EB',
    accentBg: '#EFF6FF',
    description: 'Rendimiento de primeras cuotas y conversiones',
  },
  {
    key: TAB_STUDENTS_COUNTER,
    title: 'Altas y bajas',
    icon: faUserCheck,
    accentColor: '#7C3AED',
    accentBg: '#F3E8FF',
    description: 'Histórico de inscripciones y cancelaciones',
  },
  {
    key: TAB_CITIES,
    title: 'Ciudades',
    icon: faCity,
    accentColor: '#0284C7',
    accentBg: '#E0F2FE',
    description: 'Distribución geográfica y provincias activas',
  },
  {
    key: TAB_CLASSROOMS,
    title: 'Comisiones',
    icon: faLaptop,
    accentColor: '#4F46E5',
    accentBg: '#EEF2FF',
    description: 'Estadísticas por comisiones y cursos',
  },
  {
    key: TAB_GENDER,
    title: 'Género',
    icon: faVenusMars,
    accentColor: '#E11D48',
    accentBg: '#FFE4E6',
    description: 'Distribución demográfica por género',
  },
  {
    key: TAB_AGES,
    title: 'Edades',
    icon: faBirthdayCake,
    accentColor: '#D97706',
    accentBg: '#FEF3C7',
    description: 'Promedios y grupos de edad de alumnos',
  },
];

export default function Graphics() {
  const location = useLocation();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [userLevel, setUserLevel] = useState(USER_LEVEL_START);
  const [activeTabKey, setActiveTabKey] = useState<string>(() => {
    const searchParameters = new URLSearchParams(location.search);
    return searchParameters.get('tab') || TAB_NONE;
  });

  const loadUserLevel = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await clientAxios.get('/user/checkUser');
      setUserLevel(response.data.level);
    } catch (requestError) {
      setUserLevel(USER_LEVEL_START);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const searchParameters = new URLSearchParams(location.search);
    setActiveTabKey(searchParameters.get('tab') || TAB_NONE);
  }, [location.search]);

  useEffect(() => {
    loadUserLevel();
  }, [loadUserLevel]);

  useEffect(() => {
    if (userLevel !== USER_LEVEL_START) {
      setIsLoading(false);
    }
  }, [activeTabKey, userLevel]);

  const handleSelectTab = (selectedKey: string) => {
    setActiveTabKey(selectedKey);
    history.push(`/graphic?tab=${selectedKey}`);
  };

  const renderActiveTabContent = () => {
    switch (activeTabKey) {
      case TAB_INCOMES:
        return <Incomes />;
      case TAB_SALES_METRICS:
        return <FirstInstallmentMetrics />;
      case TAB_STUDENTS_COUNTER:
        return <StudentsCounter />;
      case TAB_CITIES:
        return <Cities />;
      case TAB_CLASSROOMS:
        return <ClassroomStatistics />;
      case TAB_GENDER:
        return <Gender />;
      case TAB_AGES:
        return <AverageAges />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  const isRestrictedLevel =
    userLevel === USER_LEVEL_STATISTICS ||
    userLevel === USER_LEVEL_MENTOR_STATISTICS;

  const visibleTabs = isRestrictedLevel
    ? STATISTIC_TABS_CONFIG.filter((tabItem) => tabItem.key === TAB_STUDENTS_COUNTER)
    : STATISTIC_TABS_CONFIG;

  const currentTabConfig = STATISTIC_TABS_CONFIG.find(
    (tabItem) => tabItem.key === activeTabKey
  );

  return (
    <Container fluid="lg" className="py-4">
      {/* Header Banner */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center">
        <div>
          <div className="d-flex align-items-center mb-1">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center mr-3"
              style={{
                width: '44px',
                height: '44px',
                backgroundColor: '#EFF6FF',
                color: '#2563EB',
              }}
            >
              <FontAwesomeIcon icon={faChartPie} size="lg" />
            </div>
            <div>
              <h4 className="font-weight-bold text-dark mb-0">
                Panel de Estadísticas y Métricas
              </h4>
              <p className="text-muted small mb-0">
                Métricas integradas del rendimiento estudiantil, cobranzas y demografía
              </p>
            </div>
          </div>
        </div>
        {currentTabConfig && (
          <div className="mt-3 mt-md-0">
            <Badge
              pill
              className="px-3 py-2 font-weight-bold d-inline-flex align-items-center"
              style={{
                backgroundColor: currentTabConfig.accentBg,
                color: currentTabConfig.accentColor,
                fontSize: '13px',
                border: `1px solid ${currentTabConfig.accentColor}30`,
              }}
            >
              <FontAwesomeIcon icon={currentTabConfig.icon} className="mr-2" />
              Vista activa: {currentTabConfig.title}
            </Badge>
          </div>
        )}
      </div>

      {/* Modern Segmented Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm border p-2 mb-4">
        <div className="d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
          {visibleTabs.map((tabItem) => {
            const isTabActive = activeTabKey === tabItem.key;
            return (
              <button
                key={`tab-button-${tabItem.key}`}
                type="button"
                onClick={() => handleSelectTab(tabItem.key)}
                className="btn d-inline-flex align-items-center text-nowrap"
                style={{
                  backgroundColor: isTabActive ? '#2563EB' : 'transparent',
                  color: isTabActive ? '#ffffff' : '#475569',
                  border: isTabActive ? '1px solid #1D4ED8' : '1px solid transparent',
                  borderRadius: '10px',
                  padding: '8px 16px',
                  fontWeight: isTabActive ? 600 : 500,
                  fontSize: '13.5px',
                  boxShadow: isTabActive
                    ? '0 4px 12px rgba(37, 99, 235, 0.25)'
                    : 'none',
                  transition: 'all 0.15s ease-in-out',
                  cursor: 'pointer',
                }}
                onMouseEnter={(event: React.MouseEvent<HTMLButtonElement>) => {
                  if (!isTabActive) {
                    event.currentTarget.style.backgroundColor = '#F1F5F9';
                    event.currentTarget.style.color = '#0F172A';
                  }
                }}
                onMouseLeave={(event: React.MouseEvent<HTMLButtonElement>) => {
                  if (!isTabActive) {
                    event.currentTarget.style.backgroundColor = 'transparent';
                    event.currentTarget.style.color = '#475569';
                  }
                }}
              >
                <span
                  className="d-flex align-items-center justify-content-center mr-2"
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '6px',
                    backgroundColor: isTabActive
                      ? 'rgba(255, 255, 255, 0.2)'
                      : tabItem.accentBg,
                    color: isTabActive ? '#ffffff' : tabItem.accentColor,
                    fontSize: '11px',
                  }}
                >
                  <FontAwesomeIcon icon={tabItem.icon} />
                </span>
                {tabItem.title}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active Tab Component */}
      {renderActiveTabContent()}

      {/* Empty State Showcase */}
      {activeTabKey === TAB_NONE && !isLoading && (
        <Card className="border-0 shadow-sm rounded-lg bg-white p-4">
          <Card.Body className="text-center py-5">
            <div
              className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
              style={{
                width: '72px',
                height: '72px',
                backgroundColor: '#EFF6FF',
                color: '#2563EB',
              }}
            >
              <FontAwesomeIcon icon={faChartBar} size="2x" />
            </div>
            <h4 className="font-weight-bold text-dark mb-2">
              Seleccione una estadística para visualizar
            </h4>
            <p className="text-muted mx-auto mb-5" style={{ maxWidth: '520px' }}>
              Explore los indicadores clave de rendimiento, análisis demográficos y
              reportes financieros seleccionando una de las opciones a continuación.
            </p>

            {/* Quick Explore Cards Grid */}
            <Row className="text-left">
              {visibleTabs.map((tabItem) => (
                <Col lg={4} sm={6} key={`explore-card-${tabItem.key}`} className="mb-3">
                  <Card
                    className="h-100 border rounded-lg shadow-sm"
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      borderColor: '#E2E8F0',
                    }}
                    onClick={() => handleSelectTab(tabItem.key)}
                    onMouseEnter={(event: React.MouseEvent<HTMLDivElement>) => {
                      event.currentTarget.style.borderColor = tabItem.accentColor;
                      event.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(event: React.MouseEvent<HTMLDivElement>) => {
                      event.currentTarget.style.borderColor = '#E2E8F0';
                      event.currentTarget.style.transform = 'none';
                    }}
                  >
                    <Card.Body className="p-3 d-flex flex-column justify-content-between">
                      <div className="d-flex align-items-start mb-2">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center mr-3"
                          style={{
                            width: '42px',
                            height: '42px',
                            backgroundColor: tabItem.accentBg,
                            color: tabItem.accentColor,
                            flexShrink: 0,
                          }}
                        >
                          <FontAwesomeIcon icon={tabItem.icon} />
                        </div>
                        <div>
                          <h6 className="font-weight-bold text-dark mb-1">
                            {tabItem.title}
                          </h6>
                          <p className="text-muted small mb-0">
                            {tabItem.description}
                          </p>
                        </div>
                      </div>
                      <div className="d-flex justify-content-end align-items-center pt-2 mt-2 border-top">
                        <span
                          className="small font-weight-bold d-flex align-items-center"
                          style={{ color: tabItem.accentColor }}
                        >
                          Ver reporte <FontAwesomeIcon icon={faArrowRight} className="ml-1" />
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}
